import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../supportedLanguages';

import { Helmet } from 'react-helmet-async';

const DOC_FILES = [
  '01-presentation',
  '02-premiers-pas',
  '03-liste-notes',
  '04-mode-selection',
  '05-notes',
  '06-libelles',
  '07-lecteur-biblique',
  '08-assistant-ia',
  '09-evangile-du-jour',
  '10-annotations-personnelles',
  '11-paroles-vivantes',
  '12-partage',
  '13-mode-hors-ligne',
  '14-reglages',
  '15-gestion-compte',
  '16-surlignages',
  // Ajouté en 1.9.0 (#415). En fin de liste plutôt qu'inséré près du
  // commentaire du jour : renuméroter aurait change les slugs, donc les URL
  // déjà indexées et les liens déjà partagés.
  '17-lectures-du-jour',
];

function slugToTitle(slug: string): string {
  const parts = slug.split('-');
  const num = parseInt(parts[0]);
  const words = parts.slice(1).join(' ');
  return `${num}. ${words.charAt(0).toUpperCase() + words.slice(1)}`;
}

function fixImageSrc(src: string, lang: string): string {
  src = src.replace(/^\.\.\/\.\.\/screenshots\/[^/]+\//, `/screenshots/${lang}/`);
  src = src.replace(/^\.\.\/\.\.\/icons\//, '/icons/');
  return src;
}

const UserDocsPage = () => {
  const { lang, chapter } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const language = lang && supportedLanguages.includes(lang)
    ? lang
    : (supportedLanguages.includes(navigator.language.slice(0, 2))
        ? navigator.language.slice(0, 2)
        : 'fr');

  const currentChapter = chapter && DOC_FILES.includes(chapter) ? chapter : DOC_FILES[0];
  const currentIndex = DOC_FILES.indexOf(currentChapter);
  // Canonical URL
  const canonicalUrl = `https://bible4.me/${language}/docs/${currentChapter}`;

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const loadTitles = async () => {
      const entries = await Promise.all(
        DOC_FILES.map(async (slug) => {
          let res = await fetch(`/locales/${language}/docs/${slug}.md`);
          if (!res.ok && language !== 'fr') {
            res = await fetch(`/locales/fr/docs/${slug}.md`);
          }
          if (!res.ok) return [slug, slugToTitle(slug)];
          const text = await res.text();
          const match = text.match(/^#\s+(.+)/m);
          return [slug, match ? match[1].trim() : slugToTitle(slug)];
        })
      );
      setTitles(Object.fromEntries(entries));
    };
    loadTitles();
  }, [language]);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    const load = async () => {
      let res = await fetch(`/locales/${language}/docs/${currentChapter}.md`);
      if (!res.ok && language !== 'fr') {
        res = await fetch(`/locales/fr/docs/${currentChapter}.md`);
      }
      setContent(res.ok ? await res.text() : '');
      setLoading(false);
    };
    load();
  }, [language, currentChapter]);

  const goToChapter = (slug: string) => navigate(`/${language}/docs/${slug}`);

  const markdownComponents = {
    h1: ({ node, ...props }: any) => (
      <h1 className="text-xl font-bold text-amber-900 border-b-2 border-amber-200 pb-2 mt-10 mb-4 first:mt-0" {...props} />
    ),
    h2: ({ node, ...props }: any) => (
      <h2 className="text-base font-semibold text-amber-800 border-l-4 border-amber-400 pl-3 mt-7 mb-2" {...props} />
    ),
    h3: ({ node, ...props }: any) => (
      <h3 className="text-sm font-semibold text-amber-900 mt-5 mb-1" {...props} />
    ),
    p: ({ node, ...props }: any) => (
      <p className="text-amber-800 mb-3" {...props} />
    ),
    ul: ({ node, ...props }: any) => (
      <ul className="list-disc pl-5 mb-3 text-amber-800" {...props} />
    ),
    ol: ({ node, ...props }: any) => (
      <ol className="list-decimal pl-5 mb-3 text-amber-800" {...props} />
    ),
    li: ({ node, ...props }: any) => (
      <li className="mb-1" {...props} />
    ),
    strong: ({ node, ...props }: any) => (
      <strong className="font-semibold text-amber-950" {...props} />
    ),
    a: ({ node, href = '', children, ...props }: any) => (
      <a
        href={href}
        className="text-amber-700 hover:text-amber-900 underline"
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    ),
    img: ({ node, src = '', alt, ...props }: any) => {
      const fixedSrc = fixImageSrc(src, language);
      if (fixedSrc.includes('/icons/')) {
        return (
          <img
            src={fixedSrc}
            alt={alt}
            style={{ width: 18, height: 18, verticalAlign: 'middle', display: 'inline', margin: '0 1px -2px' }}
          />
        );
      }
      if (fixedSrc.includes('/screenshots/')) {
        return (
          <img
            src={fixedSrc}
            alt={alt}
            className="block max-w-[240px] rounded-2xl shadow-md border border-amber-200 my-4"
          />
        );
      }
      return <img src={fixedSrc} alt={alt} className="block w-24 h-24 rounded-2xl my-4" {...props} />;
    },
    table: ({ node, ...props }: any) => (
      <div className="overflow-x-auto mb-4">
        <table className="w-full border-collapse text-sm text-amber-800" {...props} />
      </div>
    ),
    thead: ({ node, ...props }: any) => (
      <thead className="bg-amber-100" {...props} />
    ),
    th: ({ node, ...props }: any) => (
      <th className="text-left font-semibold px-3 py-2 border border-amber-300 text-amber-900" {...props} />
    ),
    td: ({ node, ...props }: any) => (
      <td className="px-3 py-2 border border-amber-200" {...props} />
    ),
    tr: ({ node, ...props }: any) => (
      <tr className="even:bg-amber-50" {...props} />
    ),
  };

  return (
    <>
      <Helmet>
        <title>{titles[currentChapter] ? `${titles[currentChapter]} — Documentation Bible4Me` : `Documentation — Bible4Me`}</title>
        <meta name="description" content={t('seo.docs.description')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      {/* ...existing code... */}
      <div className="flex min-h-screen bg-amber-50 relative">
      {/* Mobile sidebar toggle button */}
      <button
        className="md:hidden fixed top-4 right-4 z-30 bg-amber-200 border border-amber-300 rounded-lg px-3 py-2 shadow text-amber-900"
        onClick={() => setSidebarOpen((open) => !open)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? '✕ Close Menu' : '☰ Menu'}
      </button>

      {/* Sidebar */}
      <nav
        className={`
          flex-col w-56 min-w-[14rem] bg-amber-100 border-r border-amber-200 sticky top-0 h-screen overflow-y-auto py-4
          z-20 transition-transform duration-200
          md:static md:flex md:translate-x-0 md:shadow-none
          ${sidebarOpen ? 'fixed left-0 top-0 h-full shadow-lg' : 'hidden'}
          md:block
        `}
        style={{ maxWidth: '80vw' }}
      >
        <div className="px-4 pb-4 border-b border-amber-200 mb-2">
          <button
            onClick={() => {
              setSidebarOpen(false);
              navigate(`/${language}`);
            }}
            className="text-amber-900 hover:underline font-medium text-sm"
          >
            {t('common.backToHome', "← Retour à l'accueil")}
          </button>
        </div>
        {DOC_FILES.map((slug) => (
          <button
            key={slug}
            onClick={() => {
              setSidebarOpen(false);
              goToChapter(slug);
            }}
            className={`block w-full text-left px-4 py-1.5 text-xs border-l-2 transition-colors ${
              slug === currentChapter
                ? 'border-amber-600 bg-amber-200 text-amber-900 font-semibold'
                : 'border-transparent text-amber-800 hover:bg-amber-200 hover:border-amber-400'
            }`}
          >
            {titles[slug] || slugToTitle(slug)}
          </button>
        ))}
      </nav>

      {/* Overlay for mobile when sidebar is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu overlay"
        />
      )}

      {/* Content */}
      <main className="flex-1 max-w-2xl px-6 md:px-12 py-10 overflow-x-hidden ml-0 md:ml-0" style={{ marginLeft: (sidebarOpen && window.innerWidth < 768) ? '14rem' : 0 }}>
        {/* Mobile back button (hidden if sidebar is open) */}
        {!sidebarOpen && (
          <div className="md:hidden mb-4">
            <button
              onClick={() => navigate(`/${language}`)}
              className="text-amber-900 hover:underline font-medium text-sm"
            >
              {t('common.backToHome', "← Retour à l'accueil")}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-900" />
          </div>
        ) : (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>

            {/* Prev / Next navigation */}
            <div className="flex justify-between mt-12 pt-6 border-t border-amber-200">
              {currentIndex > 0 ? (
                <button
                  onClick={() => goToChapter(DOC_FILES[currentIndex - 1])}
                  className="text-sm text-amber-700 hover:text-amber-900 hover:underline"
                >
                  ← {titles[DOC_FILES[currentIndex - 1]] || slugToTitle(DOC_FILES[currentIndex - 1])}
                </button>
              ) : <span />}
              {currentIndex < DOC_FILES.length - 1 && (
                <button
                  onClick={() => goToChapter(DOC_FILES[currentIndex + 1])}
                  className="text-sm text-amber-700 hover:text-amber-900 hover:underline"
                >
                  {titles[DOC_FILES[currentIndex + 1]] || slugToTitle(DOC_FILES[currentIndex + 1])} →
                </button>
              )}
            </div>
          </>
        )}
      </main>
      </div>
      {/* Liens statiques pour SEO/crawlers, placés en bas */}
      <nav aria-label="Chapitres de la documentation" className="bg-amber-50 px-4 pt-6 pb-8 max-w-2xl mx-auto">
        <ul className="flex flex-wrap gap-2 justify-center">
          {DOC_FILES.map((slug) => (
            <li key={slug}>
              <a
                href={`/${language}/docs/${slug}`}
                className={`text-xs px-2 py-1 rounded ${slug === currentChapter ? 'bg-amber-900 text-white' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
              >
                {titles[slug] || slugToTitle(slug)}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};

export default UserDocsPage;
